import { Component, computed, inject, input, output, Signal } from '@angular/core';
import { MDDropDownComponent } from '@melodic-ui/components';
import { GlobalTimezoneService } from '../../services/global-timezone.service';
import { ITimeZone } from '../../api/mylb/interfaces/itime-zone.interface';

@Component({
	selector: 'lb-timezone-selector',
	standalone: true,
	imports: [MDDropDownComponent],
	templateUrl: './timezone-selector.component.html',
	styleUrl: './timezone-selector.component.scss'
})
export class TimezoneSelectorComponent {
	private _globalTimezoneService = inject(GlobalTimezoneService);

	/** Enables compact display mode. */
	public compact = input<boolean>(false);

	/**
	 * When true, hides the dropdown and shows only the context badge.
	 * Set by the learner/instructor apps to prevent timezone changes.
	 */
	public readonlyMode = input<boolean>(false);

	/** Optional learner timezone — pinned to the top of the list with a "Learner" badge. */
	public learnerTimezone = input<ITimeZone | undefined>();

	/** Optional instructor timezone — pinned to the top of the list with an "Instructor" badge. */
	public instructorTimezone = input<ITimeZone | undefined>();

	/** Emitted whenever the user picks a different timezone. */
	public timezoneChange = output<ITimeZone>();

	/** DST-aware abbreviation for the current selected timezone (e.g. "EDT"). */
	public selectedTimezoneAbbrev = this._globalTimezoneService.selectedTimezoneAbbrev;

	// Timezone data from global service
	public timezoneContext = this._globalTimezoneService.timezoneContext;

	// Computed signal for enhanced timezone options with context highlighting
	public timezoneOptions: Signal<
		Array<{ value: number | null; label: string; abbreviation: string; searchTerms?: string; isContextTimezone?: boolean; isDivider?: boolean }>
	> = computed(() => {
		const learnerTz = this.learnerTimezone();
		const instructorTz = this.instructorTimezone();
		return this._globalTimezoneService.getAllTimezoneOptions(learnerTz, instructorTz);
	});

	// Check if timezone data is loaded
	public timezonesLoaded: Signal<boolean> = computed(() => this.timezoneOptions().length > 0);

	// Show the dropdown unless in readonly mode or no options are loaded
	public shouldShowDropdown: Signal<boolean> = computed(() => {
		if (this.readonlyMode()) return false;
		const options = this.timezoneOptions();
		if (options.length === 0) return false;

		const context = this.timezoneContext();
		// When not impersonating and device ≡ selected (functionally equivalent), hide the
		// dropdown only if we're in readonly context — otherwise always allow switching.
		// (Context pins for learner/instructor still appear at the top when provided.)
		if (context.isReadonlyMode) return false;

		return true;
	});

	/** Handles timezone selection from the dropdown. */
	public onTimezoneSelect(selectedValue: unknown): void {
		this._globalTimezoneService.setTimezoneByID(selectedValue as number);
		const selected = this.timezoneContext().selectedTimezone;
		if (selected) this.timezoneChange.emit(selected);
	}
}
